import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Button, Card, Alert, Spinner } from 'react-bootstrap';
import { FiMail, FiLock, FiEye, FiEyeOff, FiAlertCircle } from 'react-icons/fi';
import { FcGoogle } from 'react-icons/fc';
/* eslint-disable react-hooks/exhaustive-deps */
import { authAPI, userAPI } from '../utils/api';
import { saveToken, saveUser } from '../utils/auth';
import { getAppSettings, getLandingPath } from '../utils/settings';
import './LoginPage.css';

// For USER/EDITOR: go to landing page if they have 2+ departments, else normal path
const getEditorPath = async (userId) => {
  try {
    const res = await userAPI.getUserById(userId);
    const deptIds = res?.data?.departmentIds || [];
    return deptIds.length >= 2 ? '/exco-home' : getLandingPath(getAppSettings().defaultLandingPage);
  } catch {
    return getLandingPath(getAppSettings().defaultLandingPage);
  }
};

const LoginPage = ({ setIsLoggedIn, setUser }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Handle token/error from SAML SSO redirect (uses fragment to avoid nginx logging)
  useEffect(() => {
    const hash = globalThis.location.hash.slice(1); // remove leading #
    if (!hash) return;

    const params = new URLSearchParams(hash);
    const token = params.get('sso');
    const ssoError = params.get('error');

    // Clear fragment immediately from URL
    globalThis.history.replaceState({}, document.title, '/login');

    if (token) {
      (async () => {
        try {
          const parts = token.split('.');
          const payload = JSON.parse(atob(parts[1]));
          const userObj = {
            id: payload.id,
            email: payload.email,
            firstName: payload.firstName,
            lastName: payload.lastName,
            roleId: payload.roleId,
            roleType: payload.roleType,
            departmentIds: Array.isArray(payload.departmentIds) ? payload.departmentIds : [],
            excoDashboardAccess: payload.excoDashboardAccess || {},
            role: { roleType: payload.roleType },
          };
          saveToken(token);
          saveUser(userObj);
          localStorage.removeItem('selectedProject');
          setUser(userObj);
          setIsLoggedIn(true);
          let nextPath;
          if (payload.roleType === 'ADMIN') {
            nextPath = '/admin';
          } else if (payload.roleType === 'EXCO') {
            nextPath = '/exco-home';
          } else {
            nextPath = await getEditorPath(payload.id);
          }
          navigate(nextPath);
        } catch (e) {
          setError('SSO login failed. Please try again.');
        }
      })();
    } else if (ssoError) {
      const messages = {
        not_authorized: 'Your Google account is not authorized to access this application.',
        account_disabled: 'Your account has been disabled. Please contact your administrator.',
        no_email: 'Could not retrieve email from Google. Please try again.',
        domain_not_allowed: 'SSO login is only allowed for @inveniatech.com and @inveniatech.in accounts.',
        sso_failed: 'Google SSO failed. Please try again.',
      };
      setError(messages[ssoError] || 'SSO login failed. Please try again.');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const submittedForm = new FormData(e.currentTarget);
    const submittedEmail = String(submittedForm.get('email') || '').trim();
    const submittedPassword = String(submittedForm.get('password') || '');
    const emailValue = (email || submittedEmail).trim();
    const passwordValue = password || submittedPassword;

    if (!emailValue || !passwordValue) {
      setError('Email and password are required');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue)) {
      setError('Please enter a valid email address');
      return;
    }

    if (passwordValue.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (passwordValue.length > 128) {
      setError('Password cannot exceed 128 characters');
      return;
    }

    if (email !== emailValue) setEmail(emailValue);
    if (password !== passwordValue) setPassword(passwordValue);

    try {
      setLoading(true);
      const response = await authAPI.login(emailValue, passwordValue);

      if (response.success && response.data) {
        saveToken(response.data.token);
        saveUser(response.data.user);
        localStorage.removeItem('selectedProject');

        setUser(response.data.user);
        setIsLoggedIn(true);
        setSuccess('Login successful! Redirecting...');

        setTimeout(async () => {
          let nextPath;
          if (response.data.user?.roleType === 'ADMIN') {
            nextPath = '/admin';
          } else if (response.data.user?.roleType === 'EXCO') {
            nextPath = '/exco-home';
          } else {
            nextPath = await getEditorPath(response.data.user.id);
          }
          navigate(nextPath);
        }, 1500);
      }
    } catch (err) {
      console.error('Login error:', err);
      const message =
        typeof err === 'string'
          ? err
          : err?.message || err?.data?.message || 'Login failed. Please check your credentials.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-shell">
        <div className="login-left">
            <Card className="login-card shadow-lg">
              <Card.Header className="login-header text-center">
              <h1 className="login-title">DISHA</h1>
              <p className="login-subtitle">Business Intelligence System</p>
            </Card.Header>

            <Card.Body className="p-5">
              {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                  <FiAlertCircle className="me-2" />
                  {error}
                </Alert>
              )}

              {success && (
                <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                  {success}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <Button
                  type="button"
                  className="google-login-button w-100 mb-3"
                  disabled={loading}
                  onClick={() => {
                    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:62449/api';
                    globalThis.location.href = `${apiUrl.replace(/\/api\/?$/, '')}/saml2/login`;
                  }}
                >
                  <FcGoogle size={24} />
                  <span>Log in with Google</span>
                </Button>

                <div className="login-divider">
                  <span>or</span>
                </div>

                <Form.Group className="mb-3">
                  <Form.Label className="form-label">
                    <FiMail className="me-2" />
                    Email Address
                  </Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    autoComplete="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    className="form-input"
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label className="form-label">
                    <FiLock className="me-2" />
                    Password
                  </Form.Label>
                  <div className="password-input-group">
                    <Form.Control
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      autoComplete="off"
                      maxLength={128}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={loading}
                      className="form-input"
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={loading}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      aria-pressed={showPassword}
                    >
                      {showPassword ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                </Form.Group>

                <Button
                  variant="primary"
                  type="submit"
                  className="login-button w-100"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" className="me-2" />
                      Logging in...
                    </>
                  ) : (
                    'Login'
                  )}
                </Button>
              </Form>
            </Card.Body>

            <Card.Footer className="login-footer text-center text-muted">
              <small>DISHA Dashboard v1.0.0</small>
            </Card.Footer>
          </Card>
        </div>

        <div className="login-right" aria-hidden="true">
          <div className="login-right-frame">
            <img
              src="https://inveniatech.com/wp-content/uploads/2024/08/Invenia-logo.png"
              alt="Invenia"
              className="login-right-logo"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
